import React, { useEffect, useState, useCallback } from 'react';
import AdminScreen from 'components/adminScreen';
import classes from 'styles/Transaction.module.scss';
import { ROUTES } from 'config';
import { FilterListItem } from 'interfaces';
import Paginate from 'components/Paginate';
import { DATE_CONV } from 'utils/DateConv';
import { copyText } from 'utils/CopyContent';
import { Menu, MenuItem, MenuButton } from '@szhsin/react-menu';
import arrowDownBlack from 'assets/arrow-down-black.svg';
import exportFromJSON from 'export-from-json';
import { MenuDropDownFilter } from 'components/MenuDropDown';
import { flattenObject } from 'utils';
import axios from 'axios';
import { useAdmin } from 'context/admin';

const filterList: FilterListItem[] = [
	{
		field: 'date',
		label: 'Date',
		options: [
			{ value: 'Date', label: 'all' },
			{ value: '7', label: 'Last 7 days' },
			{ value: '15', label: 'Last 15 days' },
			{ value: '30', label: 'Last 30 days' },
			{ value: '90', label: 'Last 90 days' },
		],
	},
	{
		field: 'type',
		label: 'Transaction Type',
		options: [
			{ value: 'All', label: 'All Types' },
			{ value: 'deposit', label: 'Deposit' },
			{ value: 'crypto_conversion', label: 'Crypto Conversion' },
			{ value: 'refund', label: 'Refund' },
			{ value: 'adjustment', label: 'Adjustment' },
		],
	},
	{
		field: 'status',
		label: 'Status',
		options: [
			{ value: 'All', label: 'All' },
			{ value: 'pending', label: 'Pending' },
			{ value: 'processing', label: 'Processing' },
			{ value: 'completed', label: 'Completed' },
			{ value: 'failed', label: 'Failed' },
		],
	},
];

const formatType = (type: string) => {
	if (!type) return '';
	return type
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

export default function PAdminPayinTransactions() {
	const { Atoken } = useAdmin();
	const [collections, setCollections] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);

	// Filter states
	const [statusFilter, setStatusFilter] = useState<string>('');
	const [typeFilter, setTypeFilter] = useState<string>('');
	const [dateFilter, setDateFilter] = useState<string>('');
	const [searchField, setSearchField] = useState<string>('reference');
	const [searchValue, setSearchValue] = useState<string>('');

	const [rowsToExport, setRowsToExport] = useState<any[]>([]);

	const fetchTransactions = useCallback(async (pageNum: number = 1) => {
		if (loading) return;
		setLoading(true);
		try {
			const params: any = {
				page: pageNum,
				limit: 10,
				sortBy: 'createdAt',
				sortOrder: 'desc',
			};

			// Add filters if set
			if (statusFilter && statusFilter !== 'All') {
				params.status = statusFilter.toLowerCase();
			}
			if (typeFilter && typeFilter !== 'All') {
				params.type = typeFilter;
			}
			if (searchValue) {
				if (searchField === 'reference') {
					params.reference = searchValue;
				} else {
					params.userEmail = searchValue;
				}
			}
			// Date filter - calculate start_date based on days
			if (dateFilter && dateFilter !== 'Date') {
				const days = parseInt(dateFilter, 10);
				if (!isNaN(days)) {
					const startDate = new Date();
					startDate.setDate(startDate.getDate() - days);
					params.start_date = startDate.toISOString();
				}
			}

			const response = await axios.get(ROUTES.Partners_PAYIN_ALL, {
				params,
				headers: {
					Authorization: `Bearer ${Atoken}`,
				},
			});

			const data = response.data;
			setCollections(data.transactions || []);
			setPage(data.thisPage || pageNum);
			setTotal(data.total || 0);
			setHasMore(data.hasMore || false);
		} catch (error) {
			console.error('Error fetching payin transactions:', error);
		} finally {
			setLoading(false);
		}
	}, [Atoken, statusFilter, typeFilter, dateFilter, searchField, searchValue, loading]);

	useEffect(() => {
		fetchTransactions(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [statusFilter, typeFilter, dateFilter]);

	useEffect(() => {
		fetchTransactions(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		fetchTransactions(1);
	};

	const handleClearSearch = () => {
		setSearchValue('');
		// Re-fetch without search after clearing
		setTimeout(() => fetchTransactions(1), 0);
	};

	const exportToExcel = (data: any, fileName: any, exportType: any) => {
		exportFromJSON({ data, fileName, exportType });
	};

	const handleCheckboxChange = (e: any, item: any) => {
		if (e.target.checked) {
			setRowsToExport([...rowsToExport, item]);
		} else {
			setRowsToExport(rowsToExport.filter((i: any) => i._id !== item._id));
		}
	};

	const getStatusClassName = (status: string) => {
		switch (status?.toLowerCase()) {
			case 'completed':
				return classes.statusCompleted;
			case 'pending':
				return classes.statusPending;
			case 'processing':
				return classes.statusProcessing;
			case 'failed':
				return classes.statusFailed;
			default:
				return '';
		}
	};

	const lastPage = Math.ceil(total / 10);

	const paginateProps = {
		itemsCount: collections?.length || 0,
		currentPage: page,
		prevPage: page - 1,
		lastPage,
		next: hasMore ? () => fetchTransactions(page + 1) : () => {},
		prev: page > 1 ? () => fetchTransactions(page - 1) : () => {},
		jumpTo: (p: number) => fetchTransactions(p),
	};

	return (
		<AdminScreen className={classes.container}>
			<div className={classes.header}>
				<MenuDropDownFilter
					menuList={filterList[0].options.map((option) => option.value)}
					onItemSelect={(e) => {
						if (e.value !== 'Date') {
							setDateFilter(e.value);
						} else {
							setDateFilter('');
						}
					}}
				/>
				<MenuDropDownFilter
					menuList={filterList[1].options.map((option) => option.value)}
					onItemSelect={(e) => {
						if (e.value !== 'All') {
							setTypeFilter(e.value);
						} else {
							setTypeFilter('');
						}
					}}
				/>
				<MenuDropDownFilter
					menuList={filterList[2].options.map((option) => option.value)}
					onItemSelect={(e) => {
						if (e.value !== 'All') {
							setStatusFilter(e.value);
						} else {
							setStatusFilter('');
						}
					}}
				/>
				<form className={classes.userSearchForm} onSubmit={handleSearch}>
					<div className={classes.searchBarSection}>
						<div className={classes.searchInputSection}>
							<input
								type="text"
								placeholder={searchField === 'reference' ? 'Search by Transaction ID' : 'Search by Email'}
								value={searchValue}
								onChange={(e) => {
									setSearchValue(e.target.value);
									if (e.target.value === '') {
										handleClearSearch();
									}
								}}
							/>
							<Menu
								menuButton={
									<MenuButton className={classes.dropdownBtn}>
										<span>{searchField === 'reference' ? 'Txn ID' : 'Email'}</span>
										<img src={arrowDownBlack} alt="arrow-img" width={10} />
									</MenuButton>
								}
								transition
								menuClassName="drop-down"
							>
								<MenuItem
									value="reference"
									className={searchField === 'reference' ? 'dropdown-item-selected' : ''}
									onClick={() => setSearchField('reference')}
								>
									Transaction ID
								</MenuItem>
								<MenuItem
									value="user_email"
									className={searchField === 'user_email' ? 'dropdown-item-selected' : ''}
									onClick={() => setSearchField('user_email')}
								>
									User Email
								</MenuItem>
							</Menu>
						</div>
					</div>
				</form>
				<button
					onClick={() => {
						if (rowsToExport && rowsToExport?.length) {
							exportToExcel(rowsToExport, 'payin_transactions', 'csv');
						} else {
							exportToExcel(
								collections.map((cl) =>
									flattenObject(cl, '', [
										'webhook_data',
										'metadata',
										'error_details',
									]),
								),
								'payin_transactions',
								'csv',
							);
						}
					}}
				>
					Export
				</button>
			</div>

			<div className={classes.transaction_module}>
				<table>
					<thead>
						<tr>
							<th>Date</th>
							<th>User ID</th>
							<th>User Email</th>
							<th>Transaction ID</th>
							<th>Type</th>
							<th>Amount</th>
							<th>Currency</th>
							<th>Status</th>
							<th>Export</th>
						</tr>
					</thead>
					<tbody>
						{collections &&
							collections.map((tx: any) => {
								return (
									<tr key={tx._id}>
										<td onDoubleClick={() => copyText(DATE_CONV.dateConvert(tx.createdAt))}>
											{DATE_CONV.dateConvert(tx.createdAt)}
										</td>
										<td onDoubleClick={() => copyText(tx.partner_userID)}>
											{tx.partner_userID}
										</td>
										<td onDoubleClick={() => copyText(tx.user_email)}>
											{tx.user_email}
										</td>
										<td onDoubleClick={() => copyText(tx.encryptus_order_id)}>
											{tx.encryptus_order_id}
										</td>
										<td onDoubleClick={() => copyText(tx.transferType)}>
											{formatType(tx.transferType)}
										</td>
										<td onDoubleClick={() => copyText(tx.fiatAmount)}>
											{tx.fiatAmount}
										</td>
										<td onDoubleClick={() => copyText(tx.fiatCurrency)}>
											{tx.fiatCurrency}
										</td>
										<td>
											<span className={getStatusClassName(tx.transaction_status)}>
												{tx.transaction_status}
											</span>
										</td>
										<td>
											<input
												type="checkbox"
												onChange={(e) => handleCheckboxChange(e, tx)}
												checked={
													rowsToExport.find((i: any) => i._id === tx._id)
														? true
														: false
												}
											/>
										</td>
									</tr>
								);
							})}
					</tbody>
				</table>
			</div>
			{total > 0 && <Paginate {...paginateProps} />}
		</AdminScreen>
	);
}
